import { esc } from '../esc';
import { feeCents, formatMoney } from '../methods';
import {
  formatCardInput, formatIbanInput, isValidCardNumber, isValidIban, normaliseCardNumber, normaliseIban,
} from '../payout-validation';
import { normalise, fieldError } from '../billing-address';
import { requestWithdrawal, toPaymentError } from '../payments-api';
import type { CreateWithdrawalResponse, PayoutMethod, PayoutMethodId, WithdrawalLimits } from '../types';
import { ALERT_ERR_CLS, ALERT_OK_CLS, BTN_CLS, FIELD_ERR_CLS, INPUT_CLS, LABEL_CLS } from './classes';

export interface GameCurrency {
  name: string;
  /** How many game-currency units per 1 EUR (e.g. 100 = 100 DV per EUR). */
  perEur: number;
  /** Player's current balance in game-currency units. */
  balance: number;
}

export interface WithdrawFormOptions {
  methods: PayoutMethod[];
  limits: WithdrawalLimits;
  firstName?: string | null;
  lastName?: string | null;
  /** Currency the host UI displays; a EUR-only note is shown when it is not EUR. */
  displayCurrency?: string;
  billingHref?: string;
  /** When set, the amount input is in game-currency units with EUR conversion shown. */
  gameCurrency?: GameCurrency;
  onSubmitted?: (res: CreateWithdrawalResponse) => void;
}

export interface WithdrawFormHandle {
  destroy(): void;
  getMethod(): PayoutMethodId | null;
}

/** Friendly text for backend error codes; falls back to the server message. */
export function withdrawalErrorMessage(code: string, fallback: string, details?: Record<string, unknown>): string {
  switch (code) {
    case 'WITHDRAWALS_DISABLED': return 'Withdrawals are currently unavailable.';
    case 'PAYOUT_METHOD_UNAVAILABLE': return 'This payout method is not available right now.';
    case 'INVALID_RECEIVER': return 'The card number or IBAN is not valid.';
    case 'INSUFFICIENT_FUNDS': return 'You do not have enough withdrawable balance.';
    case 'EXCEEDS_DEPOSITS': {
      const rem = typeof details?.remainingCents === 'number' ? ` You can still withdraw ${formatMoney(details.remainingCents, 'EUR')}.` : '';
      return `You can only withdraw up to the amount you have deposited.${rem}`;
    }
    case 'WITHDRAWAL_IN_PROGRESS': return 'You already have a withdrawal in progress.';
    case 'AMOUNT_OUT_OF_RANGE': return 'The amount is outside the allowed range.';
    case 'EMAIL_NOT_VERIFIED': return 'Please verify your email first.';
    case 'KYC_REQUIRED': return 'Identity verification is required before withdrawing.';
    case 'BILLING_ADDRESS_REQUIRED': return 'Please complete your billing address first.';
    case 'PAYOUT_FAILED': return 'The payout could not be sent. Please try again later.';
    default: return fallback;
  }
}

/** "12", "12.5", "12,50" -> cents; null when not a valid amount. */
export function parseAmountToCents(s: string): number | null {
  const t = s.trim();
  if (!/^\d+([.,]\d{1,2})?$/.test(t)) return null;
  const [w, f = ''] = t.replace(',', '.').split('.');
  return Number(w) * 100 + Number((f + '00').slice(0, 2));
}

export function mountWithdrawForm(el: HTMLElement, opts: WithdrawFormOptions): WithdrawFormHandle {
  const limits = opts.limits;
  const methods = opts.methods.filter((m) => !limits.methods || limits.methods.includes(m.id));
  let method: PayoutMethod | null = methods[0] ?? null;
  let destroyed = false;

  const nameField = (name: string, label: string, value: string | null | undefined, ac: string) => `
    <label class="block">
      <span class="${LABEL_CLS} mb-1.5">${esc(label)}</span>
      <input type="text" name="${name}" value="${esc(value ?? '')}" autocomplete="${ac}" class="${INPUT_CLS}" />
      <span data-error="${name}" class="${FIELD_ERR_CLS}" hidden></span>
    </label>`;

  if (!methods.length) {
    el.innerHTML = `<p class="text-sm text-text-dim">Withdrawals are not available right now.</p>`;
    return { destroy() { el.innerHTML = ''; }, getMethod: () => null };
  }

  el.innerHTML = `
    <form class="w-full flex flex-col gap-3.5" novalidate>
      ${opts.displayCurrency && opts.displayCurrency !== 'EUR'
        ? `<p data-role="eur-note" class="text-[13px] text-text-dim">Withdrawals are processed in EUR, regardless of the currency shown elsewhere.</p>` : ''}
      <div class="flex gap-2" role="radiogroup" aria-label="Payout method">
        ${methods.map((m) => `
          <button type="button" data-method="${esc(m.id)}" role="radio"
            class="flex-1 flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 bg-surface border rounded touch-manipulation transition-colors duration-[120ms]">
            <span>${esc(m.label)}</span>
          </button>`).join('')}
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        ${nameField('firstName', 'First name', opts.firstName, 'given-name')}
        ${nameField('lastName', 'Last name', opts.lastName, 'family-name')}
      </div>
      <label class="block">
        <span data-role="receiver-label" class="${LABEL_CLS} mb-1.5"></span>
        <input type="text" name="receiver" inputmode="text" autocomplete="off" autocapitalize="characters" spellcheck="false" class="${INPUT_CLS}" />
        <span data-error="receiver" class="${FIELD_ERR_CLS}" hidden></span>
      </label>
      ${opts.gameCurrency ? `
      <label class="block">
        <span class="${LABEL_CLS} mb-1.5">Amount (${esc(opts.gameCurrency.name)})</span>
        <input type="text" name="gameAmount" inputmode="numeric" autocomplete="off" placeholder="0" class="${INPUT_CLS}" />
        <span data-error="gameAmount" class="${FIELD_ERR_CLS}" hidden></span>
        <span data-role="eur-conversion" class="block mt-1 text-xs text-text-dim"></span>
        <input type="hidden" name="amount" />
      </label>` : `
      <label class="block">
        <span class="${LABEL_CLS} mb-1.5">Amount (EUR)</span>
        <input type="text" name="amount" inputmode="decimal" autocomplete="off" placeholder="0.00" class="${INPUT_CLS}" />
        <span data-error="amount" class="${FIELD_ERR_CLS}" hidden></span>
        <span class="block mt-1 text-xs text-text-dim">
          Min ${esc(formatMoney(limits.minCents, 'EUR'))} · Max ${esc(formatMoney(Math.min(limits.maxCents, limits.availableCents), 'EUR'))}
        </span>
      </label>`}
      <p data-role="preview" class="text-sm text-text-dim" aria-live="polite"></p>
      <div data-role="error" class="${ALERT_ERR_CLS}" role="alert" hidden></div>
      <div data-role="ok" class="${ALERT_OK_CLS}" role="status" hidden></div>
      <button type="submit" class="${BTN_CLS}" ${limits.hasOpenRequest ? 'disabled' : ''}>Request withdrawal</button>
      ${limits.hasOpenRequest ? `<p class="text-[13px] text-text-dim">You already have a withdrawal in progress.</p>` : ''}
    </form>`;

  const form = el.querySelector('form') as HTMLFormElement;
  const inp = (n: string) => form.elements.namedItem(n) as HTMLInputElement;
  const q = (r: string) => el.querySelector(`[data-role="${r}"]`) as HTMLElement;
  const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;

  const setErr = (field: string, msg: string | null) => {
    const s = el.querySelector(`[data-error="${field}"]`) as HTMLElement;
    s.textContent = msg ?? '';
    s.hidden = !msg;
  };
  const clearAll = () => {
    const fields = ['firstName', 'lastName', 'receiver', gc ? 'gameAmount' : 'amount'];
    fields.forEach((f) => setErr(f, null));
    q('error').hidden = true;
    q('ok').hidden = true;
  };

  const renderMethod = () => {
    el.querySelectorAll<HTMLElement>('[data-method]').forEach((b) => {
      const on = b.dataset.method === method?.id;
      b.setAttribute('aria-checked', String(on));
      b.classList.toggle('border-gold', on);
      b.classList.toggle('shadow-glow', on);
      b.classList.toggle('border-border', !on);
    });
    const isCard = method?.receiver === 'cardNumber';
    q('receiver-label').textContent = isCard ? 'Card number' : 'IBAN';
    inp('receiver').inputMode = isCard ? 'numeric' : 'text';
    inp('receiver').value = '';
    setErr('receiver', null);
  };

  const gc = opts.gameCurrency;
  const gameUnitsToEurCents = (units: number) => gc ? Math.floor((units * 100) / gc.perEur) : 0;

  const amountCents = (): number | null => {
    if (gc) {
      const units = parseInt(inp('gameAmount').value) || 0;
      return units > 0 ? gameUnitsToEurCents(units) : null;
    }
    return parseAmountToCents(inp('amount').value);
  };

  const renderConversion = () => {
    if (!gc) return;
    const units = parseInt(inp('gameAmount').value) || 0;
    const convEl = q('eur-conversion');
    if (units <= 0) { convEl.textContent = ''; inp('amount').value = ''; return; }
    const eurCents = gameUnitsToEurCents(units);
    const fee = feeCents(eurCents, limits.feeBps);
    const minUnits = Math.ceil((limits.minCents * gc.perEur) / 100);
    const maxCents = Math.min(limits.maxCents, limits.availableCents);
    const maxUnits = Math.floor((maxCents * gc.perEur) / 100);
    inp('amount').value = (eurCents / 100).toFixed(2);
    convEl.textContent = `= ${formatMoney(eurCents, 'EUR')} · Fee ${formatMoney(fee, 'EUR')} · You receive ${formatMoney(eurCents - fee, 'EUR')} · Balance: ${gc.balance.toLocaleString()} ${gc.name} · Min ${minUnits.toLocaleString()} · Max ${maxUnits.toLocaleString()}`;
  };

  const renderPreview = () => {
    if (gc) { renderConversion(); return; }
    const a = amountCents();
    const p = q('preview');
    if (a === null || a <= 0) { p.textContent = ''; return; }
    const fee = feeCents(a, limits.feeBps);
    p.textContent = `Fee ${formatMoney(fee, 'EUR')} · You receive ${formatMoney(a - fee, 'EUR')}`;
  };

  const receiverError = (): string | null => {
    const v = inp('receiver').value;
    if (method?.receiver === 'cardNumber') {
      return isValidCardNumber(normaliseCardNumber(v)) ? null : 'Enter a valid card number';
    }
    return isValidIban(normaliseIban(v)) ? null : 'Enter a valid IBAN';
  };

  const onClick = (e: Event) => {
    const b = (e.target as HTMLElement).closest('[data-method]') as HTMLElement | null;
    if (!b) return;
    const m = methods.find((x) => x.id === b.dataset.method);
    if (m && m !== method) { method = m; renderMethod(); }
  };
  const onReceiverInput = () => {
    const i = inp('receiver');
    i.value = method?.receiver === 'cardNumber' ? formatCardInput(i.value) : formatIbanInput(i.value);
    setErr('receiver', null);
  };
  const onAmountInput = () => { setErr('amount', null); renderPreview(); };
  const onGameAmountInput = () => { setErr('gameAmount', null); renderPreview(); };

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    clearAll();
    if (!method) return;
    const first = normalise(inp('firstName').value);
    const last = normalise(inp('lastName').value);
    let bad = false;
    for (const [f, v] of [['firstName', first], ['lastName', last]] as const) {
      const err = v ? fieldError(f, v) : 'Required';
      if (err) { setErr(f, err); bad = true; }
    }
    const rErr = receiverError();
    if (rErr) { setErr('receiver', rErr); bad = true; }
    const a = amountCents();
    const amtField = gc ? 'gameAmount' : 'amount';
    if (a === null || a <= 0) { setErr(amtField, 'Enter a valid amount'); bad = true; }
    else if (gc) {
      const units = parseInt(inp('gameAmount').value) || 0;
      if (units > gc.balance) { setErr('gameAmount', `Exceeds balance (${gc.balance.toLocaleString()} ${gc.name})`); bad = true; }
      else if (a < limits.minCents) {
        const minUnits = Math.ceil((limits.minCents * gc.perEur) / 100);
        setErr('gameAmount', `Minimum is ${minUnits.toLocaleString()} ${gc.name}`); bad = true;
      } else if (a > Math.min(limits.maxCents, limits.availableCents)) {
        const maxCents = Math.min(limits.maxCents, limits.availableCents);
        const maxUnits = Math.floor((maxCents * gc.perEur) / 100);
        setErr('gameAmount', `Maximum is ${maxUnits.toLocaleString()} ${gc.name}`); bad = true;
      }
    }
    else if (a < limits.minCents) { setErr('amount', `Minimum is ${formatMoney(limits.minCents, 'EUR')}`); bad = true; }
    else if (a > Math.min(limits.maxCents, limits.availableCents)) {
      setErr('amount', `Maximum is ${formatMoney(Math.min(limits.maxCents, limits.availableCents), 'EUR')}`); bad = true;
    }
    if (bad || a === null) return;

    submitBtn.disabled = true;
    try {
      const res = await requestWithdrawal({
        method: method.id,
        amountCents: a,
        firstName: first ?? undefined,
        lastName: last ?? undefined,
        ...(method.receiver === 'cardNumber'
          ? { cardNumber: normaliseCardNumber(inp('receiver').value) }
          : { iban: normaliseIban(inp('receiver').value) }),
      });
      if (destroyed) return;
      q('ok').textContent = res.message || 'Withdrawal requested.';
      q('ok').hidden = false;
      inp('receiver').value = '';
      inp('amount').value = '';
      if (gc) inp('gameAmount').value = '';
      renderPreview();
      // A request is now open on the server: keep the button disabled.
      opts.onSubmitted?.(res);
    } catch (err) {
      if (destroyed) return;
      submitBtn.disabled = false;
      const pe = toPaymentError(err);
      q('error').textContent = withdrawalErrorMessage(pe.code, pe.message, pe.details);
      q('error').hidden = false;
    }
  };

  el.addEventListener('click', onClick);
  inp('receiver').addEventListener('input', onReceiverInput);
  if (gc) inp('gameAmount').addEventListener('input', onGameAmountInput);
  else inp('amount').addEventListener('input', onAmountInput);
  form.addEventListener('submit', onSubmit);
  renderMethod();

  return {
    destroy() {
      destroyed = true;
      el.removeEventListener('click', onClick);
      inp('receiver').removeEventListener('input', onReceiverInput);
      if (gc) inp('gameAmount').removeEventListener('input', onGameAmountInput);
      else inp('amount').removeEventListener('input', onAmountInput);
      form.removeEventListener('submit', onSubmit);
      el.innerHTML = '';
    },
    getMethod: () => method?.id ?? null,
  };
}
